
import { useState, useEffect } from 'react';
import StandardContentRenderer from './StandardContentRenderer';
import SpecialArticleRenderer from './SpecialArticleRenderer';
import ContentLoading from './ContentLoading';
import ContentError from './ContentError';
import ContentEmpty from './ContentEmpty';
import { useContentProcessor } from '@/hooks/content-processor';
import { isProductionBuild } from '@/utils/productionFallbacks';

interface ContentRendererProps {
  content: string;
  isMarkdown: boolean;
  slug?: string;
}

/**
 * Main content renderer that handles special article renderers and content processing
 */
const ContentRenderer = ({ content, isMarkdown, slug }: ContentRendererProps) => {
  const [shouldUseSpecialRenderer, setShouldUseSpecialRenderer] = useState(false);
  const isProduction = isProductionBuild();
  
  // Process content using the content processor hook with proper config
  const { processedContent, isLoading, error } = useContentProcessor(content, { slug: slug || '' });
  
  useEffect(() => {
    // Check if we should use a special renderer
    if (slug) {
      const specialSlugs = [
        'harmful-content-detection', 
        'youtube-recommendation-system', 
        'candidate-generation',
        'candidate-generation-part2'
      ];
      setShouldUseSpecialRenderer(specialSlugs.includes(slug));
    } else {
      setShouldUseSpecialRenderer(false);
    }
  }, [slug]);
  
  // Handle loading state
  if (isLoading) {
    return <ContentLoading />;
  }
  
  // Handle error state
  if (error) {
    console.error("Content processing error:", error);
    return <ContentError message={error} />;
  }
  
  // Handle empty content
  if (!processedContent && !content) {
    return <ContentEmpty />;
  }
  
  // Cast processedContent to string since our components expect string content
  const contentString = processedContent ? String(processedContent) : content;
  
  // Use special renderer for certain articles, or standard renderer for most articles
  return shouldUseSpecialRenderer ? (
    <SpecialArticleRenderer 
      content={contentString} 
      slug={slug || ''} 
      isProduction={isProduction}
      isMarkdown={isMarkdown}
    />
  ) : (
    <StandardContentRenderer 
      content={contentString} 
      isMarkdown={isMarkdown}
      slug={slug} 
    />
  );
};

export default ContentRenderer;
